import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Card, Container, Divider, Grid} from "@mantine/core";
import {useCallback} from "react";
import {useApp} from "../../../../providers/RegionProvider/RegionProvider";
import {EntityTypeSelect} from "../../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {
  SiteSiteAssignmentFormFieldValues,
  SiteSiteAssignmentFormProvider,
  SiteSiteAssignmentFormValues
} from "../SiteSiteAssignmentForm/form-context";
import {
  getDefaultSiteSiteAssignmentFormValues,
  getSiteSiteAssignmentFormValues,
  SiteSiteAssignmentFormFields
} from "../SiteSiteAssignmentForm/SiteSiteAssignmentForm";
import {EntityMultiSelect} from "../../../select/entity/EntityMultiSelect/EntityMultiSelect";
import {toTransportCategory, TransportCategory} from "@ots-frontend/ots-data";
import {TransportCategorySelect} from "../../../select/entity/TransportCategorySelect/TransportCategorySelect";

interface SiteSiteAssignmentBulkFormProps {
  options?: SiteSiteAssignmentBulkFormOptions;
  defaultValues?: Partial<SiteSiteAssignmentBulkFormValues>;
  onSubmit?: (values: SiteSiteAssignmentBulkFormValues) => void;
  submitting?: boolean;
}

export interface SiteSiteAssignmentBulkFormOptions {
  entitySelectionDisabled?: boolean
  categorySelectionDisabled?: boolean
}

export interface SiteSiteAssignmentBulkFormValues extends SiteSiteAssignmentFormValues {
  entityType: string
  entityIds: string[]
  category: TransportCategory
}

const getDefaultValues = (defaultValues: Partial<SiteSiteAssignmentBulkFormValues> | undefined): SiteSiteAssignmentFormFieldValues => {
  return {
    ...getDefaultSiteSiteAssignmentFormValues(defaultValues),
    entityType: defaultValues?.entityType,
    entityIds: defaultValues?.entityIds,
    category: defaultValues?.category?.code,
  }
}

export const SiteSiteAssignmentBulkForm = (props: SiteSiteAssignmentBulkFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {regionId} = useApp()

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {

      const formValues: SiteSiteAssignmentBulkFormValues = {
        ...getSiteSiteAssignmentFormValues(values),
        entityType: values.entityType!,
        entityIds: values.entityIds!,
        category: toTransportCategory(values.category!),
      }

      onSubmit(formValues);
    }
  }, [form, onSubmit]);

  return (
    <SiteSiteAssignmentFormProvider form={form}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <Grid>
          <Grid.Col span={8}>
            <Container fluid={true}>
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <TransportCategorySelect
                  label={t("common.entity.siteassignment.form.field.category", "Category")}
                  placeholder={t<string>("common.entity.siteassignment.form.field.object.category", "Category")}
                  withinPortal={true}
                  disabled={options?.categorySelectionDisabled}
                  clearable
                  withAsterisk
                  required
                  {...form.getInputProps("category")} />
                <SiteSiteAssignmentFormFields/>
                <Divider my="sm" variant="dotted"/>
                <Button type="submit" loading={submitting} fullWidth mt="xl">
                  {t("common.action.submit", {ns: "common"})}
                </Button>
              </Card>
            </Container>
          </Grid.Col>
          <Grid.Col span={4}>
            <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
              <EntityTypeSelect
                options={['ROUTE', 'ROUTEGROUP', 'OPERATINGAREA']}
                label={t("common.entity.siteassignment.form.field.object", "Object")}
                placeholder={t<string>("common.entity.siteassignment.form.field.object.placeholder", "Object")}
                withinPortal={true}
                disabled={options?.entitySelectionDisabled}
                clearable
                withAsterisk
                required
                {...form.getInputProps("entityType")} />
              <EntityMultiSelect
                pt="md"
                regionId={regionId}
                entityType={form.getTransformedValues().entityType}
                withinPortal={true}
                disabled={options?.entitySelectionDisabled}
                clearable
                withAsterisk
                required
                {...form.getInputProps("entityIds")} />
            </Card>
          </Grid.Col>
        </Grid>
      </form>
    </SiteSiteAssignmentFormProvider>
  );
};
